/* eslint-disable no-unused-vars, no-console, react/jsx-indent-props,react/jsx-wrap-multilines, no-undef, react/jsx-closing-bracket-location */

import { InfiniteStoryBase, WithPreview } from "@quintype/components";
import get from "lodash/get";
import { object, shape, string } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { LatestStories, RelatedStories, TrendingStories } from "../story-templates/atoms";
import AdWrapper from "../story-templates/atoms/AdWrapper";
import GnReach from "../story-templates/gn-reach/gn-story-template";
import LiveBlog from "../story-templates/live-blog";
import Recipe from "../story-templates/recipe";
import { Text } from "../story-templates/text";
import { getCardByAttribute } from "../utils/story";
import { getQueryParamsForArticleApi } from "../utils/utils";
import ProgressBar from "../story-templates/atoms/ProgressBar";
import { useDeviceType } from "../custom-hooks/useDeviceType";

import "./story.m.css";

function StoryPageBase(props) {
  // Can switch to a different template based story-template, or only show a spoiler if index > 0
  // The text story template is used in all scenarios as there are no visual changes.
  const { story, config, index: storyIndex } = props;

  switch (story["story-template"]) {
    case "text":
    case "video":
    case "interview":
    case "review":
    case "listicle":
    case "story-listicle":
    case "photo":
      return <Text story={story} config={config} params={{ disableUpNext: true }} storyIndex={storyIndex} />;
    case "recipe":
      return <Recipe story={story} config={config} />;
    case "live-blog":
      return <LiveBlog story={story} config={config} storyIndex={storyIndex} />;
    case "gn-reach":
      return <GnReach story={story} config={config} />;
    case "premium":
      return <Text story={story} config={config} params={{ disableUpNext: true }} />;
    default:
      return <Text story={story} config={config} storyIndex={storyIndex} />;
  }
}

StoryPageBase.propTypes = {
  story: object,
  config: object,
};

function InfiniteScrollStoryPageBase(props) {
  // For Infinite Scroll
  // Can switch to a different template based story-template, or only show a spoiler if index > 0
  // The text story template is used in all scenarios as there are no visual changes.

  const { story, config, params, index } = props;
  switch (story["story-template"]) {
    case "text":
    case "video":
    case "interview":
    case "review":
    case "listicle":
    case "story-listicle":
    case "photo":
      return <Text story={story} config={config} params={params} storyIndex={index} />;
    case "live-blog":
      return <LiveBlog story={story} config={config} storyIndex={index} />;
    case "premium":
      return <Text story={story} config={config} params={params} storyIndex={index} />;
    case "gn-reach":
    case "recipe":
      return null;
    default:
      return <Text story={story} config={config} params={params} storyIndex={index} />;
  }
}

const FIELDS =
  "id,metadata,headline,slug,url,hero-image-s3-key,hero-image-metadata,first-published-at,last-published-at,alternative,published-at,author-name,author-id,sections,authors,story-template,cards,access,attributes,linked-stories,linked-story-ids";
async function storyPageLoadItems(pageNumber, story) {
  if (pageNumber > 1) {
    return [];
  }
  const response = await global
    .wretch(`/api/v1/stories?section-id=${story.sections[0].id}`)
    .query({
      fields: FIELDS,
      limit: 5,
      offset: 5 * pageNumber,
    })
    .get()
    .json();

  return response.stories.map((story) => {
    return { story, currentPath: `/${story.slug}`, otherProp: "value" };
  });
}
async function loadAlsoInPackageItems(pageNumber, story) {
  const alsoInPackageItems = getCardByAttribute(story, "Also_In_This_Package");

  const ids = alsoInPackageItems["story-elements"]?.map((element) => element.metadata?.["linked-story-id"]);

  const response = await Promise.all(ids.map((id) => global.wretch(`/api/v1/stories/${id}`).get().json()));
  const stories = response.map((e) => e.story);

  return stories.map((story) => ({ story, currentPath: `/${story.slug}`, otherProp: "value" }));
}

export function StoryPage(props) {
  const { story, config, relatedStories } = get(props, ["data"], {});
  const articleApi = get(props, ["config", "articleApi"], false);
  const alsoInPackageItems = getCardByAttribute(story, "Also_In_This_Package");

  const currentPath = get(props, ["currentPath"]);
  // Please remove because this is not needed. Keeping it so that something internal does not break.
  const params = getQueryParamsForArticleApi(currentPath);

  const [singleInfiniteStory, setSingleInfiniteStory] = useState({});
  const template = get(story, ["story-template"], "");
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const adsConfig = get(qtState, ["config", "ads-config"], {});

  const sideAd1 = get(adsConfig, ["slots", "sidebar_right_1"], {});
  const sideAd2 = get(adsConfig, ["slots", "sidebar_right_2"], {});
  const sideAd3 = get(adsConfig, ["slots", "sidebar_right_3"], {});

  const scrollRef = useRef(null);
  const currentInfiniteStoryRef = useRef(null);

  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const isMobile = deviceType === "mobile";

  useEffect(() => {
    if (scrollRef.current && singleInfiniteStory?.index) {
      const currentStory = scrollRef.current.querySelector(`[data-infinite-scroll="${singleInfiniteStory?.index}"]`);
      currentInfiniteStoryRef.current = currentStory;
    }
  }, [singleInfiniteStory]);

  return (
    <div className={`container story-container${articleApi ? " story-margin-top" : ""}`}>
      {story["story-template"] === "gn-reach" || story["story-template"] === "recipe" || articleApi ? (
        <>
          <StoryPageBase story={story} config={config} />
          {story["story-template"] !== "gn-reach" && articleApi && (
            <RelatedStories extraPadding={true} relatedStories={relatedStories} />
          )}
        </>
      ) : (
        <>
          <ProgressBar
            story={singleInfiniteStory?.story}
            scrollRef={currentInfiniteStoryRef}
            storyIndex={singleInfiniteStory?.index}
            params={params}
          />
          <div className="story-wrapper" styleName="story-wrapper" ref={scrollRef}>
            <div>
              <InfiniteStoryBase
                {...props}
                neverHideItem={true}
                render={(props) => <InfiniteScrollStoryPageBase {...props} params={params} />}
                loadItems={alsoInPackageItems ? loadAlsoInPackageItems : storyPageLoadItems}
                onInitialItemFocus={(item) =>
                  app.registerPageView(
                    { pageType: "story-page", data: { story: item?.story } },
                    `/${item?.story?.slug}`
                  )
                }
                onItemFocus={(item, index) => {
                  // console.log(`Story In View: ${item?.story?.headline}`);
                  setSingleInfiniteStory({ story: item?.story, index: index });
                  const authorsName = item?.story?.authors?.map((author) => author.name);
                  const sectionsName = item?.story?.sections?.map((section) => section.name);
                  if (index > 0) {
                    dataLayer.push({
                      event: "trackvirtual",
                      virtualPage: `/${item?.story?.slug}`,
                      virtualSections: sectionsName,
                      virtualAuthor: authorsName,
                      virtualPublishedDate: item?.story?.["published-at"]
                        ? new Date(item?.story?.["published-at"]).toISOString().split("T")[0]
                        : "",
                      virtualPageTitle: item?.story?.headline,
                    });
                  }
                }}
                changeUrlTo={(item) => item?.currentPath || props?.currentPath}
              />
              <RelatedStories extraPadding={true} relatedStories={relatedStories} />
            </div>
            <div styleName="divider"></div>
            <div styleName="story-wrapper-right" id="parent-right-section">
              <AdWrapper adStyleName="story-page-ad-slot" id={sideAd1?.id} />
              <TrendingStories />
              <AdWrapper adStyleName="story-page-ad-slot" id={sideAd2?.id} />

              <LatestStories />

              <div styleName="sticky-ad-rhs">
                <AdWrapper adStyleName="story-page-ad-slot" id={sideAd3?.id} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

StoryPage.propTypes = {
  data: shape({
    story: object,
    config: object,
  }),
  currentPath: string,
};

export const StoryPagePreview = WithPreview(StoryPage, (data, story) =>
  Object.assign({}, data, {
    story,
  })
);
